import React, { useEffect, useState, useRef } from 'react';
import uuid from 'uuid';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import Select from 'components/input/select';
import Chip from '@material-ui/core/Chip';
import { IconButton, InputAdornment } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import FilledInput from '@material-ui/core/FilledInput';
import CloseIcon from '@material-ui/icons/Close';
import colors from 'theme/colors';
import { IFieldOption, EProductField } from 'store/field-options/types';
import DeleteModal from './delete-modal';

const { activeBackground } = colors;

export interface ProductMenuList {
  availableProducts: IFieldOption[] | undefined;
  updateProductField: Function;
  index: number;
  updateFieldOptions: Function;
  deleteProduct: Function;
  name: string | undefined;
  productLine: string | undefined;
}

const useStyles = makeStyles({
  customMenu: {
    width: '240px',
    paddingLeft: '30px',
    background: `${activeBackground}`,
  },
});

const InputField = withStyles({
  root: {
    background: `${activeBackground} !important`,
    width: '100%',
  },
  input: {
    background: `${activeBackground}`,
    padding: '7px 7px 7px 27px',
  },
  inputAdornedEnd: {
    background: `${activeBackground}`,
  },
  adornedEnd: {
    background: `${activeBackground}`,
  },
})(FilledInput);

const DeletedChip = withStyles({
  root: {
    width: '100%',
    borderRadius: 'unset',
    justifyContent: 'space-between',
    background: 'none',
  },
})(Chip);

const getProductsWithProductLine = (availableProducts: IFieldOption[] | undefined, productLine: string | undefined) => {
  return availableProducts?.filter((avp: IFieldOption) => avp.productLine === productLine) ?? [];
};

const getSelectedName = (availableProducts: IFieldOption[] | undefined, name: string | undefined) => {
  return availableProducts?.find((value: { name: string }) => value.name === name);
};

const setSelectedDisplayName = (
  availableProducts: IFieldOption[] | undefined,
  name: string | undefined,
  setDisplayValue: Function
) => {
  const selectedName = getSelectedName(availableProducts, name);
  if (selectedName?.displayName) {
    setDisplayValue(selectedName.displayName);
  }
};

const checkAddCustomEvent = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
  return event.target.value === 'Add Custom' || event.target.value === '';
};

const checkAvailableProductsName = (availableProducts: IFieldOption[] | undefined, name: string | undefined) => {
  return availableProducts && !availableProducts.find((value: { name: string }) => value.name === name);
};

export default (props: ProductMenuList) => {
  const { availableProducts, updateProductField, index, updateFieldOptions, deleteProduct, name, productLine } = props;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [isCustom, setCustom] = useState(false);
  const [chipValues, setChipValue] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [displayValue, setDisplayValue] = useState(name);
  const [filteredValue, setFilteredValue] = useState(availableProducts);
  const textInput = useRef<HTMLDivElement>(null);
  const productsWithProductLine = getProductsWithProductLine(availableProducts, productLine);

  useEffect(() => {
    setSelectedDisplayName(availableProducts, name, setDisplayValue);
  }, [name, availableProducts]);

  useEffect(() => {
    setFilteredValue(productsWithProductLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsWithProductLine.length, setFilteredValue]);

  useEffect(() => {
    if (isCustom) {
      updateProductField(index, EProductField.NAME, '');
    }
  }, [isCustom, index, updateProductField]);

  const handleInputChanges = (event: React.ChangeEvent<{ name: string | undefined; value: string }>) => {
    event.stopPropagation();
    updateProductField(index, EProductField.NAME, event.target.value);
    setDisplayValue(event.target.value);
  };

  const handleClose = () => {
    setOpen(false);
    if (isCustom) {
      updateProductField(index, EProductField.NAME, '');
      setCustom(false);
    }
  };

  const handleOpen = () => setOpen(true);

  const handleClickDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const stopImmediatePropagation = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    event.stopPropagation();
    event.preventDefault();
  };
  function handleInputFocus() {
    if (textInput.current) {
      textInput.current.focus();
    }
  }

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    event.preventDefault();
    if (checkAddCustomEvent(event)) {
      setCustom(true);
      handleOpen();
      handleInputFocus();
      stopImmediatePropagation(event);
    } else {
      updateProductField(index, EProductField.NAME, event.target.value);
    }
  };

  const handleClick = () => {
    // Disallow duplicate values
    if (checkAvailableProductsName(availableProducts, name)) {
      updateFieldOptions();
      setCustom(false);
    }
  };

  const handleDelete = (chipValue: string) => {
    setChipValue(chipValue);
    handleClickDialogOpen();
  };

  return (
    <>
      <Select
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        value={name}
        onChange={handleChange}
        renderValue={() => displayValue}
      >
        {isCustom ? (
          <InputField
            id="input-volume-value"
            type="text"
            disableUnderline
            autoFocus
            value={name}
            inputRef={textInput}
            onChange={handleInputChanges}
            placeholder="Type..."
            onKeyDown={e => e.stopPropagation()}
            /* eslint-disable react/jsx-wrap-multilines */
            endAdornment={
              <InputAdornment position="end">
                <IconButton color="inherit" onClick={handleClick}>
                  <CheckIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        ) : (
          <MenuItem value="Add Custom" key="Add_Custom" className={classes.customMenu}>
            Add Custom
          </MenuItem>
        )}
        {filteredValue ? (
          filteredValue.map((availableProduct: IFieldOption) => (
            <MenuItem
              key={`available-product-list-${uuid()}`}
              value={availableProduct.name}
              onKeyDown={e => e.stopPropagation()}
            >
              <DeletedChip
                label={availableProduct.displayName}
                onDelete={() => handleDelete(availableProduct.name)}
                deleteIcon={<CloseIcon />}
                onClick={() => setDisplayValue(availableProduct.displayName)}
              />
            </MenuItem>
          ))
        ) : (
          <MenuItem>No products</MenuItem>
        )}
        {openDialog && (
          <DeleteModal
            openDialog={openDialog}
            handleDialogClose={handleDialogClose}
            chipValues={chipValues}
            deleteProduct={deleteProduct}
            setChipValue={setChipValue}
            name={name}
            index={index}
            updateProductField={updateProductField}
          />
        )}
      </Select>
    </>
  );
};
